import { createPortal } from 'react-dom'
import FilterModal from '../../../../assets/frontend/js/components/FilterModal'
import { CustomSearchFilter } from '../../../../assets/frontend/js/components/SearchFilter'
import GroupFilter from '../../../../assets/frontend/js/components/GroupFilter'
import TypeFilter from './TypeFilter'
import { memo, useRef } from 'react'
import { _x } from '@wordpress/i18n'
import TimeFrameFilter from './TimeFrameFilter'
import { WP_LOCALE } from '../../../../assets/utils/algolia/algoliaLocalization'
import { getFilterLayoutFlags, useConfigContext } from './ConfigContext'
import TagFilter from '../../../../assets/frontend/js/components/TagFilter'
import FilterToggle from '../../../../assets/frontend/js/components/FilterToggle'
import LayoutToggle from '../../../../assets/frontend/js/components/LayoutToggle'
import classNames from 'classnames'
import { useDisplayController } from '../../../../assets/frontend/js/hooks/dom'
import { useFilters } from './EventsFiltersController'

type FilterPanelProps = {
  layout: 'card' | 'list'
  onLayoutChange: (layout: 'card' | 'list') => void
  collapsed: boolean
  isDesktop: boolean
  modalId: string
}

const FilterPanel = memo(function FilterPanel(props: FilterPanelProps) {
  const { icons: groupIcons, typePreFilter, filterLayout } = useConfigContext()
  const { showGroupFilter, showFilterBar } = getFilterLayoutFlags(filterLayout)

  const filterBarRef = useRef<HTMLDivElement>(null)

  useDisplayController(filterBarRef)

  const { type: typeFilterValue } = useFilters()

  return (
    <>
      <div className="filter-panel" data-bs-theme="dark">
        <div>
          <CustomSearchFilter
            placeholder={_x(
              'Which event are you looking for?',
              'Events List Search Filter Placeholder',
              'icf'
            )}
          />
        </div>
        {showFilterBar && (
          <div
            ref={filterBarRef}
            className={classNames('filter-bar', {
              show: !props.collapsed
            })}
          >
            <div className="filter-bar__content">
              {showGroupFilter && (
                <GroupFilter
                  ariaLabel={_x(
                    'Filter by group',
                    'Events List Group Filter ARIA Label',
                    'icf'
                  )}
                  attribute={`groups.title.${WP_LOCALE}`}
                  icons={groupIcons}
                />
              )}
              <div className="filter-panel-button-group d-none d-md-flex ms-auto">
                {!typePreFilter && (
                  <TypeFilter
                    ariaLabel={_x(
                      'Filter by offer type',
                      'Events List Type Filter ARIA Label',
                      'icf'
                    )}
                  />
                )}
                <FilterToggle variant="desktop" modalId={props.modalId} />
                <LayoutToggle
                  variant="desktop"
                  value={props.layout}
                  onChange={props.onLayoutChange}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      {showFilterBar &&
        createPortal(
          <FilterModal
            title={_x('Filter Events', 'Filter Modal Title', 'icf')}
            id={props.modalId}
          >
            {!typePreFilter && (
              <div className="d-md-none filter-modal-filter-section">
                <TypeFilter
                  title={_x('Offer type', 'Filter Modal Filter Title', 'icf')}
                  ariaLabel={_x(
                    'Filter by offer type',
                    'Events List Type Filter ARIA Label',
                    'icf'
                  )}
                />
              </div>
            )}
            {typeFilterValue !== 'offers' && (
              <div className="filter-modal-filter-section filter-modal-filter-section--with-divider">
                <TimeFrameFilter
                  title={_x('Date', 'Filter Modal Filter Title', 'icf')}
                  ariaLabel={_x(
                    'Filter by date',
                    'Filter Modal Filter ARIA Label',
                    'icf'
                  )}
                  ids={['today', 'tomorrow', 'week', 'nextWeek', 'nextMonth']}
                />
              </div>
            )}
            <div className="filter-modal-filter-section">
              <TagFilter
                attribute={`languages.fullName.${WP_LOCALE}`}
                limit={5}
                title={_x('Language', 'Filter Modal Filter Title', 'icf')}
                ariaLabel={_x(
                  'Filter by language',
                  'Filter Modal Filter ARIA Label',
                  'icf'
                )}
              />
            </div>
          </FilterModal>,
          document.body
        )}
    </>
  )
})

export default FilterPanel
